import type { ScheduleMode } from "@/interfaces/CampaignBuilder";
import { toLocalDateStr } from "@/lib/reminders";
import { isoToZonedSlot, zonedWallTimeToIso } from "@/utils/timezone-helper";

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** Tomorrow, local time, "YYYY-MM-DD" — the Step 2 default date and `StyledInput`'s default `min`. */
export function defaultScheduleDate(now: Date = new Date()): string {
  return toLocalDateStr(addDays(now, 1));
}

/** Minutes a same-day scheduled send must be ahead of now (GFG-432). */
export const SCHEDULE_LEAD_MINUTES = 60;

const MS_PER_MINUTE = 60_000;

/** The first valid send instant: floor(now to the minute) + lead + 1 minute (a minute ≤ now + lead is too soon). */
function earliestScheduleInstant(now: Date): Date {
  const flooredMs = Math.floor(now.getTime() / MS_PER_MINUTE) * MS_PER_MINUTE;
  return new Date(flooredMs + (SCHEDULE_LEAD_MINUTES + 1) * MS_PER_MINUTE);
}

const MINUTES_PER_DAY = 24 * 60;
/** Upper bound for the walk past the fall-back repeat hour (at most 60 repeated minutes). */
const MAX_REPEAT_WALK_STEPS = 120;

function nextMinute(time: string): string | undefined {
  const [hours, minutes] = time.split(":").map(Number);
  const total = (hours ?? 0) * 60 + (minutes ?? 0) + 1;
  if (total >= MINUTES_PER_DAY) return undefined;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** Earliest valid "HH:mm" when `date` is today in `timeZone` (floor(now to the minute) + 61 min, read in `timeZone`); skips wall minutes that `isScheduleTooSoon` reads as already gone (fall-back repeat hour). undefined for any other day, and undefined when no valid minute is left today. */
export function scheduleMinTime(
  date: string,
  timeZone: string,
  now: Date = new Date(),
): string | undefined {
  const today = isoToZonedSlot(now.toISOString(), timeZone).date;
  const earliest = isoToZonedSlot(earliestScheduleInstant(now).toISOString(), timeZone);
  if (date !== today || earliest.date !== today) return undefined;
  let candidate: string | undefined = earliest.time;
  for (let step = 0; candidate !== undefined && step <= MAX_REPEAT_WALK_STEPS; step += 1) {
    if (!isScheduleTooSoon(date, candidate, timeZone, now)) return candidate;
    candidate = nextMinute(candidate);
  }
  return undefined;
}

/** Today in `timeZone` ("YYYY-MM-DD") while a valid minute is left today, else tomorrow in `timeZone`. */
export function scheduleMinDate(timeZone: string, now: Date = new Date()): string {
  const today = isoToZonedSlot(now.toISOString(), timeZone).date;
  if (scheduleMinTime(today, timeZone, now) !== undefined) return today;
  const tomorrow = new Date(`${today}T00:00:00Z`);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  return tomorrow.toISOString().slice(0, 10);
}

/** The time the API stores for an empty `scheduleTime` (`combineDateTime` in campaign-builder-serializer). */
const SERVER_DEFAULT_SEND_TIME = "09:00";

/** date + time read in `timeZone` is ≤ now + 60 min. An empty time reads as the 09:00 the API will store, so a cleared time is judged as the server judges it. Empty/unparsable date, or unparsable time → false. */
export function isScheduleTooSoon(
  date: string,
  time: string,
  timeZone: string,
  now: Date = new Date(),
): boolean {
  const iso = zonedWallTimeToIso(date, time || SERVER_DEFAULT_SEND_TIME, timeZone);
  if (iso === null) return false;
  return new Date(iso).getTime() <= now.getTime() + SCHEDULE_LEAD_MINUTES * MS_PER_MINUTE;
}

/** B5: the time to keep after the user picks `date` — `time` when it is not too soon, else scheduleMinTime(date) when defined, else `time` unchanged. */
export function scheduleTimeAfterDatePick(
  date: string,
  time: string,
  timeZone: string,
  now: Date = new Date(),
): string {
  if (!isScheduleTooSoon(date, time, timeZone, now)) return time;
  return scheduleMinTime(date, timeZone, now) ?? time;
}

/** "Schedule for later" with no date picked. Only "certain" carries a single date; the other modes never miss one. */
export function isScheduleDateMissing(mode: ScheduleMode, date: string): boolean {
  return mode === "certain" && date.trim() === "";
}

const CUSTOM_EXPIRY_DAYS = 60;

/** Today + 60 days, local time, "YYYY-MM-DD" — mirrors the `expiryMode` default of `"60"`. */
export function defaultCustomExpiryDate(now: Date = new Date()): string {
  return toLocalDateStr(addDays(now, CUSTOM_EXPIRY_DAYS));
}
